import { Component, ElementRef, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertConditionCard } from '../../../../models/api/alerts/alert-condition-card';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ClientSelectorComponent } from '../../../core/controls/client-selector/client-selector.component';
import { FilterInputComponent } from '../../../core/controls/filter-input.component';
import { SelectButtonComponent } from '../../../core/controls/select-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { AlertConditionComponent } from '../alert-condition.component';
import { SearchFilterStatusComponent } from '../../../core/labels/search-filter-status.component';

@Component({
  selector: 'app-alert-condition-step',
  imports: [
    ActionRowComponent,
    ButtonComponent,
    EmptyStateComponent,
    PageHeaderComponent,
    FilterInputComponent,
    AlertConditionComponent,
    SelectButtonComponent,
    LoadingPanelComponent,
    RequiredFieldsNoteComponent,
    ClientSelectorComponent,
    SearchFilterStatusComponent,
  ],
  template: `
    <app-search-filter-status
      [message]="statusMessage"
      [statusRevision]="statusRevision"
    />
    <div class="tw-py-8">
      <app-loading-panel
        loadingStyle="Box"
        [state]="lob && !conditionsLoading ? 'Loaded' : 'Loading'"
      >
        <ng-template #loaded>
          <div>
            <app-page-header label="Select an alert type" headerSize="h2">
              <ng-template #right>
                <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
              </ng-template>
            </app-page-header>
            @if (client?.length > 1) {
              <div class="tw-flex tw-pt-4 tw-w-full">
                <app-client-selector
                  [control]="fg.controls.client"
                  [label]="isEditMode ? 'Client (cannot be changed)' : 'Client'"
                />
              </div>
            }
            @if (!fg.controls.client.value && client?.length > 1) {
              <div class="tw-pt-6">
                <app-empty-state
                  icon="fa-bell fa-light"
                  height="658px"
                  label="Available conditions"
                  message="Select a client to view conditions"
                  backgroundImage="url('/assets/images/MovementArrow658.webp')"
                  backgroundSize="45% 70%"
                />
              </div>
            } @else {
              <div>
                <div
                  class="tw-pt-4 tw-pb-8 tw-flex tw-flex-col-reverse tw-items-start tw-gap-4 md:tw-flex-row md:tw-justify-between"
                >
                  <app-filter-input
                    [control]="filter"
                    label="Search alert types"
                    recordLabel="type"
                    recordLabelPlural="types"
                    [recordCount]="filtered.length"
                    classes="tw-w-full md:tw-w-[400px]"
                    [vertical]="true"
                  />
                  <div class="tw-w-full tw-min-w-0 md:tw-w-auto">
                    <app-select-button
                      class="tw-w-full"
                      [control]="lobs"
                      [options]="filteredLobs"
                      optionLabel="value"
                      optionAriaLabel="description"
                      optionValue="value"
                      [multiple]="true"
                    />
                  </div>
                </div>

                <div
                  role="radiogroup"
                  aria-label="Select an alert type"
                  class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 md:tw-gap-6 tw-max-h-[60vh] md:tw-max-h-[550px] tw-overflow-y-auto md:tw-pr-4"
                >
                  @for (condition of filtered; track condition.type) {
                    <app-alert-condition
                      [condition]="condition"
                      [filterValue]="filterValue"
                      [control]="fg.controls.condition"
                      (dblclick)="next()"
                      (doubleEnter)="next()"
                    />
                  }
                </div>

                <div class="tw-py-4"></div>
              </div>
            }
          </div>

          <app-action-row>
            <app-button
              right2
              label="Next"
              (click)="next()"
              [disabled]="!fg.valid"
            />
          </app-action-row>
          <div
            left1
            class="tw-flex tw-flex-col tw-text-sm tw-italic tw-max-w-[6000px] tw-pt-8"
          >
            <span>*All alerts that count days use calendar days.</span>
            <span>
              **Alerts are not sent to Client users for Montana workers'
              compensation claims when carrier policy information present.</span
            >
          </div>
        </ng-template>
      </app-loading-panel>
    </div>
  `,
  styles: `
    @media (max-width: 768px) {
      :host ::ng-deep app-select-button .p-selectbutton {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.25rem;
      }

      :host ::ng-deep app-select-button .p-selectbutton .p-button,
      :host ::ng-deep app-select-button .p-selectbutton .p-togglebutton {
        flex: unset;
        min-width: 0;
        width: 100%;
      }
    }
  `,
})
export class AlertConditionStepComponent
  extends BaseComponent
  implements OnInit
{
  private alerts = inject(AlertsService);
  private el = inject(ElementRef);

  private readonly restrictedConditionMap: Record<string, string[]> = {
    CauseCode: ['CauseCode', 'CauseDesc'],
    NatureResult: ['NatureResultCode', 'NatureResultDesc'],
    BodyPart: ['PartTargetCode', 'PartTargetDesc'],
  };

  @Input()
  stepper: StepperComponent;

  lob: ListItem[];
  filteredLobs: ListItem[];
  client: ListItem[];
  filter = new FormControl<string>(null);
  lobs = new FormControl<string[]>([]);
  conditions: AlertConditionCard[] = [];
  filtered: AlertConditionCard[] = [];
  filterValue: string;
  conditionsLoading = false;
  isEditMode = false;
  protected statusMessage = '';
  protected statusRevision = 0;
  fg = new FormGroup({
    lob: new FormControl<string[]>([]),
    client: new FormControl<ListItem>(null, Validators.required),
    condition: new FormControl<AlertConditionCard>(null, Validators.required),
  });

  next() {
    const alert = this.alerts.currentAlert();
    if (!alert) {
      return;
    }

    const selectedCondition = this.fg.controls.condition.value;
    const previousCardType = alert.conditionCard?.type;
    const previousClientNum = alert.contracts?.[0]?.contNum?.toString() ?? null;
    const clientValue = this.fg.controls.client.value;
    const currentClientNum = clientValue?.value ?? null;

    const clientChanged =
      previousClientNum != null &&
      currentClientNum != null &&
      previousClientNum !== currentClientNum;
    const conditionChanged =
      previousCardType != null && previousCardType !== selectedCondition?.type;

    if (clientChanged) {
      this.alerts.resetAllOptions(alert);
    } else if (conditionChanged) {
      this.alerts.resetConditionOptions(alert);
    }

    alert.conditionCard = selectedCondition;

    const clientName = this.extractClientName(clientValue?.description ?? '');

    if (clientValue) {
      alert.contracts = [
        {
          contNum: parseInt(clientValue.value, 10),
          description: clientName,
        },
      ];
    }

    (alert as any).client = clientValue;
    (alert as any).clientDescription = clientName;

    // Keep only LOBs supported by the selected condition (e.g. drop DS/LA for Lag Time).
    // In edit mode the LOB control is filter-only and left empty, so preserve saved LOBs.
    const supportedLobs = selectedCondition?.lobs || [];
    let selectedLobs = this.isEditMode
      ? alert.linesOfBusiness?.length > 0
        ? alert.linesOfBusiness
        : (alert.linesOfBusinessCard ?? [])
      : this.lobs.value || [];
    if (supportedLobs.length > 0) {
      selectedLobs = selectedLobs.filter((lob) =>
        supportedLobs.includes(lob),
      );
    }
    alert.linesOfBusinessCard = selectedLobs;
    alert.linesOfBusiness = selectedLobs;
    this.stepper.next();
  }

  private scrollToSelectedCondition(): void {
    const selected = this.fg.controls.condition.value;
    if (!selected) return;

    setTimeout(() => {
      const container = this.el.nativeElement.querySelector(
        '.tw-overflow-y-auto',
      );
      const cards = container?.querySelectorAll('app-alert-condition');
      if (!cards) return;

      const index = this.filtered.findIndex((c) => c.type === selected.type);
      const card = cards[index] as HTMLElement;
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  /**
   * Extracts the client name from a description that may include the contract number.
   * E.g., "1197 - New York City Health + Hospitals" -> "New York City Health + Hospitals"
   */
  private extractClientName(description: string): string {
    if (!description) {
      return '';
    }
    const dashIndex = description.indexOf(' - ');
    if (dashIndex > -1) {
      return description.substring(dashIndex + 3);
    }
    return description;
  }

  async ngOnInit() {
    const alert = this.alerts.currentAlert();

    this.isEditMode = this.alerts.isEditMode();

    // load LOBs and clients
    const listResponse = await this.api.list.get();
    this.lob = listResponse.linesOfBusiness;
    this.filterLobs();
    this.client = listResponse.clients;

    // restore previous values if navigating back or editing
    if (alert) {
      const storedClient = alert.contracts?.[0]?.contNum?.toString() ?? null;
      if (storedClient) {
        const storedClientItem = this.client.find(
          (x) => x.value === storedClient,
        );
        this.fg.controls.client.setValue(storedClientItem);
      }

      if (!this.isEditMode) {
        const storedLobs = alert.linesOfBusinessCard ?? [];
        this.lobs.setValue(storedLobs);
      }
    }

    // auto-select client if only one and not already set
    if (this.client?.length === 1 && !this.fg.controls.client.value) {
      this.fg.controls.client.setValue(this.client[0]);
    }
    // Disable client selection in edit mode
    if (this.isEditMode) {
      this.fg.controls.client.disable();
    }
    // load alert conditions
    await this.loadConditions();
    // restore selected condition
    if (alert?.conditionCard) {
      const matchingCondition = this.conditions.find(
        (c) => c.type === alert.conditionCard?.type,
      );
      this.fg.controls.condition.setValue(matchingCondition ?? null);
    }
    this.setFiltered();
    this.scrollToSelectedCondition();

    // subscribe to filter changes
    this.dispose.subs = this.filter.valueChanges.subscribe(() => {
      this.setFiltered();
    });

    // subscribe to LOB filter changes
    this.dispose.subs = this.lobs.valueChanges.subscribe(async () => {
      await this.loadConditions();
      this.setFiltered();
      this.announceFilteredCount();
    });

    // When client changes, clear the selected condition card so the user must re-select
    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(() => {
      if (!this.isEditMode) {
        this.fg.controls.condition.reset(null);
      }
    });
  }

  // load conditions from DB and apply UI metadata from JSON
  private async loadConditions(): Promise<void> {
    this.conditionsLoading = true;
    try {
      const availableLobs =
        this.filteredLobs?.map((item) => item.value) ??
        this.lob?.map((item) => item.value) ??
        [];

      const selectedLobs = this.lobs.value || [];
      const primaryLob = selectedLobs[0] ?? availableLobs[0] ?? '';

      this.conditions = await this.alerts.loadConditionCards(primaryLob);
    } finally {
      this.conditionsLoading = false;
    }
  }

  // filter out 'AD' LOB
  filterLobs() {
    this.filteredLobs = this.lob.filter((item) => item.value !== 'AD');
  }

  // filter and sort conditions
  private setFiltered() {
    const filterText = this.filter.value?.toLowerCase() || '';
    const selectedLobs = this.lobs.value || [];
    const filtered = this.conditions.filter((condition) => {
      const matchesText =
        !filterText ||
        condition.title?.toLowerCase().includes(filterText) ||
        condition.description?.toLowerCase().includes(filterText);
      const matchesLob =
        selectedLobs.length === 0 ||
        condition.lobs?.some((lob) => selectedLobs.includes(lob));
      const allowedBySecurity = !this.isConditionRestricted(condition.type);
      return matchesText && matchesLob && allowedBySecurity;
    });

    this.filtered = filtered;
    this.filterValue = filterText;
  }

  private announceFilteredCount(): void {
    const count = this.filtered.length;
    this.statusMessage =
      count === 0
        ? 'No types found'
        : count === 1
          ? '1 type found'
          : `${count} types found`;
    this.statusRevision++;
  }

  private isConditionRestricted(type?: string | null): boolean {
    if (type == null) {
      return false;
    }

    const restrictedFields =
      this.session.user?.fieldSecurity?.restrictedFields || [];
    const requiredFields = this.restrictedConditionMap[type] || [];
    return requiredFields.some((field) => restrictedFields.includes(field));
  }
}
